import React, { useEffect, useRef, useState } from 'react';
import {
  useAuthStore,
  useCheckoutStore,
  useErrorTrackerHandler,
  useLanguageStore,
  useLocationStore,
  useMenuStore,
  useNavigation,
  useParams,
  useTranslation,
  useWindowSize,
} from 'mycheck-core';
import {
  Background,
  CustomButton,
  CustomIcon,
  Header,
  Overlay,
} from '@components';
import styles from './OrderStatusPage.module.scss';
import OrderStatusDescription from './components/OrderStatusDescription/OrderStatusDescription';
import OrderStatusNotifications from './components/OrderStatusNotifications/OrderStatusNotifications';
import OrderStatusTracker from './components/OrderStatusTracker/OrderStatusTracker';
import { type OrderGuestStatusType } from 'plugins/CheckoutPlugin/types/CheckoutTypes';
import isEqual from 'lodash/isEqual';
import { CartStoreService } from 'core/core/service/CartStoreService';
import OrderRestaurantDetails from './components/OrderRestaurantDetails/OrderRestaurantDetails';

const POLLING_INTERVAL_TIME = 15000;

export const OrderStatusPage: React.FC = () => {
  const [orderStatusData, setOrderStatusData] =
    useState<OrderGuestStatusType | null>(null);
  const [loading, setLoading] = useState(true);
  const { isLg } = useWindowSize();
  const navigation = useNavigation();
  const { t } = useTranslation();
  const AuthStore = useAuthStore();
  const CheckoutStore = useCheckoutStore();
  const LanguageStore = useLanguageStore();
  const LocationStore = useLocationStore();
  const MenuStore = useMenuStore();
  const { orderStatusId } = useParams<{ orderStatusId: string }>();
  const errorHandler = useErrorTrackerHandler();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const selectedLanguage = LanguageStore.selectedValue;

  const onClose = () => {
    navigation.push('/menu');
  };

  const tick = (cb: () => Promise<void>) => {
    intervalRef.current = setTimeout(cb, POLLING_INTERVAL_TIME);
  };

  const getOrderStatus = async () => {
    try {
      const orderStatusResponse = await CheckoutStore.getGuestOrderStatus(
        orderStatusId,
        AuthStore.accessToken,
      );
      if (!isEqual(orderStatusResponse.data, orderStatusData)) {
        setOrderStatusData(orderStatusResponse.data);
      }
    } catch (error) {
      errorHandler.onError(error);
    } finally {
      clearTimeout(intervalRef.current);
      tick(getOrderStatus);
    }
  };

  useEffect(() => {
    // Bucket clean up
    MenuStore.clearIsAdult();
    CartStoreService.clearStore();
    CheckoutStore.clearCheckoutWithTime();
    CheckoutStore.clearRoomTableParams();

    let isMounted = true;
    const isFetchOrderStatusAvailable = !!(
      AuthStore.isGuestLoggedIn &&
      (!isLg || LocationStore.selectedExperience)
    );

    // Fetch order status data
    const fetchOrderStatus = () => {
      CheckoutStore.getGuestOrderStatus(orderStatusId, AuthStore.accessToken)
        .then(async (orderStatusResponse) => {
          setOrderStatusData(orderStatusResponse.data);
          try {
            if (
              selectedLanguage === 'en_US' ||
              !LanguageStore?.experienceTranslations?.[selectedLanguage]
            ) {
              await LocationStore.fetchExperience(
                orderStatusResponse.data.experienceId,
              );
            } else {
              const selectedLanguageTranslations =
                LanguageStore.experienceTranslations[selectedLanguage];
              const languageTranslations = selectedLanguageTranslations.find(
                (language) =>
                  Number(language.resource_id) ===
                  orderStatusResponse.data.experienceId,
              );

              LocationStore.setGuestOrderStatusMessage(
                languageTranslations?.translation?.settings?.guest_order_status
                  ?.status_message ?? null,
              );
            }
          } catch (error) {
            errorHandler.onError(error);
          }
        })
        .catch((error) => {
          onClose();
          errorHandler.onError(error);
        })
        .finally(() => {
          if (isMounted) {
            tick(getOrderStatus);
            setLoading(false);
          }
        });
    };
    // TODO remove this condition after implementing token retry mechanism
    if (!AuthStore.isGuestLoggedIn && !isLg) {
      (async () =>
        await AuthStore.logInGuest(selectedLanguage, errorHandler.onError).then(
          () => {
            fetchOrderStatus();
          },
        ))();
    }
    if (isFetchOrderStatusAvailable) {
      fetchOrderStatus();
    }
    return () => {
      isMounted = false;
      clearTimeout(intervalRef.current);
    };
  }, []);

  const renderBody = () => {
    return (
      <div className={styles.orderStatusPage}>
        <Header
          enforceSmall
          color="#ffffff"
          headerTitle={t('orderStatus.header')}
          hideBackButton
        />
        {loading ? (
          <div className={styles.orderStatusPageLoaderContainer}>
            <CustomIcon name="loader" />
          </div>
        ) : (
          <Background secondary className={styles.orderStatusPageScroll}>
            <OrderStatusDescription
              title={
                orderStatusData.statuses[orderStatusData.statuses.length - 1]
                  .name
              }
              orderId={orderStatusData.orderId}
              dueTime={orderStatusData.dueTime}
            />
            <OrderStatusTracker orderStatusesDate={orderStatusData.statuses} />
            <OrderStatusNotifications
              maskedEmail={orderStatusData.maskedEmail}
              maskedPhone={orderStatusData.maskedPhone}
              refetchOrderStatus={getOrderStatus}
            />
            <OrderRestaurantDetails />
            <CustomButton
              text={t('orderStatus.button')}
              className={styles.orderStatusPageCustomButton}
              handleButtonClick={onClose}
              testId="confirm-done"
            />
          </Background>
        )}
      </div>
    );
  };

  if (isLg) {
    return (
      <Overlay
        width={480}
        height={780}
        testId="confirm-page"
        onClickOutside={onClose}
      >
        {renderBody()}
      </Overlay>
    );
  }

  return renderBody();
};
